import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, BookOpen, Clock, Award, ArrowRight, Mail, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import SEO from '../components/SEO';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const DashboardPage = () => {
  const { user, profile } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const [enrollRes, subjectsRes] = await Promise.all([
          fetch('/api/enrollments/my', {
            headers: { 'Authorization': `Bearer ${session?.access_token}` }
          }).then(r => r.json()),
          supabase.from('subjects').select('*')
        ]);
        setEnrollments(Array.isArray(enrollRes) ? enrollRes : []);
        if (subjectsRes.data) setSubjects(subjectsRes.data);
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [user]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'active':
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aspire-red"></div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-surface" data-testid="dashboard-page">
      <SEO title="Dashboard" path="/dashboard" noIndex />
      {/* Header */}
      <section className="bg-charcoal py-12">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-aspire-red flex items-center justify-center">
                <span className="text-white font-heading font-bold text-2xl">
                  {profile?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-2xl text-white">
                  Welcome, {profile?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Student'}
                </h1>
                <p className="text-gray-400 text-sm">{user?.email}</p>
              </div>
            </div>
            <Link 
              to="/igcse" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="browse-courses-btn"
            >
              Browse Courses
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-aspire-red/10 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-aspire-red" />
              </div>
              <div>
                <div className="font-heading font-bold text-2xl">{enrollments.length}</div>
                <div className="text-sm text-gray-500">Enrolled Courses</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 flex items-center justify-center">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="font-heading font-bold text-2xl">
                  {enrollments.filter(e => e.status === 'active').length}
                </div>
                <div className="text-sm text-gray-500">Active Courses</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <div className="font-heading font-bold text-2xl">
                  {subjects.length}
                </div>
                <div className="text-sm text-gray-500">Available Courses</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-spacing">
        <div className="container-main">
          <Tabs defaultValue="enrollments" className="w-full">
            <TabsList className="bg-white border border-gray-200 mb-8">
              <TabsTrigger value="enrollments" className="data-[state=active]:bg-aspire-red data-[state=active]:text-white px-6" data-testid="enrollments-tab">
                My Courses
              </TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-aspire-red data-[state=active]:text-white px-6" data-testid="profile-tab">
                Profile
              </TabsTrigger>
            </TabsList>

            {/* Enrollments Tab */}
            <TabsContent value="enrollments">
              {enrollments.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {enrollments.map((enrollment) => {
                    const subject = subjects.find(s => s.subject_id === enrollment.batch_id?.split('_')[0]) || {};
                    return (
                      <div 
                        key={enrollment.enrollment_id} 
                        className="bg-white border border-gray-200 p-6"
                        data-testid={`enrollment-card-${enrollment.enrollment_id}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="font-heading font-bold text-lg">{subject.name || 'Course'}</h3>
                          <span className={`text-xs font-mono px-2 py-1 ${getStatusColor(enrollment.status)}`}>
                            {enrollment.status}
                          </span>
                        </div>
                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                          <p>Level: {subject.level || 'N/A'}</p>
                          <p>Exam Board: {subject.exam_board || 'N/A'}</p>
                          <p>Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}</p>
                        </div>
                        <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                          <span className={`text-xs font-mono px-2 py-1 ${getStatusColor(enrollment.payment_status)}`}>
                            Payment: {enrollment.payment_status}
                          </span>
                          {enrollment.status === 'active' && (
                            <Link to={`/igcse/${subject.slug}`} className="text-aspire-red text-sm font-bold hover:underline">
                              View Course →
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-white border border-gray-200">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-xl mb-2">No Courses Yet</h3>
                  <p className="text-gray-500 mb-6">You haven't enrolled in any courses yet.</p>
                  <Link to="/igcse" className="btn-primary inline-block" data-testid="browse-courses-empty">
                    Browse Courses
                  </Link>
                </div>
              )}
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="bg-white border border-gray-200 p-8 max-w-2xl">
                <div className="flex items-center gap-6 mb-8 pb-8 border-b border-gray-200">
                  <div className="w-24 h-24 bg-aspire-red flex items-center justify-center">
                    <span className="text-white font-heading font-bold text-3xl">
                      {profile?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-2xl">{profile?.name || user?.email?.split('@')[0] || 'Student'}</h2>
                    <p className="text-gray-500 capitalize">{profile?.role || 'Student'}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Email Address</p>
                      <p className="font-medium">{user?.email || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Phone Number</p>
                      <p className="font-medium">{profile?.phone || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Member Since</p>
                      <p className="font-medium">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
};

export default DashboardPage;
